import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CachedUserData {
    username?: string;
    email?: string;
    status?: string;
    balance?: number;
}

@Injectable({
    providedIn: 'root'
})
export class UserCacheService {
    private readonly cache = new Map<string, CachedUserData>();
    private cache$ = new BehaviorSubject<Map<string, CachedUserData>>(this.cache);

    constructor() { }

    /**
     * Get user data from cache
     * @param userId User ID to retrieve
     * @returns Cached user data or null if not found
     */
    getUser(userId: string): CachedUserData | null {
        return this.cache.get(userId) || null;
    }

    /**
     * Set user data in cache
     * @param userId User ID
     * @param data User data to cache
     */
    setUser(userId: string, data: Partial<CachedUserData>): void {
        const existingData = this.cache.get(userId) || {};
        this.cache.set(userId, {
            ...existingData,
            ...data
        });
        this.cache$.next(this.cache);
    }

    /**
     * Check if user data exists in cache
     * @param userId User ID to check
     * @returns true if user data exists in cache
     */
    hasUser(userId: string): boolean {
        return this.getUser(userId) !== null;
    }

    /**
     * Get username from cache
     * @param userId User ID
     * @returns Username or null if not found
     */
    getUsername(userId: string): string | null {
        return this.getUser(userId)?.username || null;
    }

    /**
     * Get email from cache
     * @param userId User ID
     * @returns Email or null if not found
     */
    getEmail(userId: string): string | null {
        return this.getUser(userId)?.email || null;
    }

    /**
     * Get cache as observable
     * @returns Observable of the cache map
     */
    getCache$(): Observable<Map<string, CachedUserData>> {
        return this.cache$.asObservable();
    }

    /**
     * Clear all cache
     */
    clear(): void {
        this.cache.clear();
        this.cache$.next(this.cache);
    }

    /**
     * Clear cache for specific user
     * @param userId User ID to remove from cache
     */
    clearUser(userId: string): void {
        this.cache.delete(userId);
        this.cache$.next(this.cache);
    }
}
